import React from 'react'
import { connect } from 'react-redux'
import { Select, Button, Table, Progress, Alert, Card, Row, Col, message } from 'antd'
import AdminLayout from '../../components/AdminLayout'

// Simple Statistic replacement for antd v3.12.x which doesn't have Statistic component
const SimpleStatistic = ({ title, value }) => (
  <div>
    <div style={{ color: 'rgba(0, 0, 0, 0.45)', fontSize: 14, marginBottom: 4 }}>{title}</div>
    <div style={{ color: 'rgba(0, 0, 0, 0.85)', fontSize: 24, fontWeight: 500 }}>{value}</div>
  </div>
)
import SectionHeader from '../../components/SectionHeader'
import SectionHeaderTemplate from '../../components/SectionHeaderTemplate'
import SectionContent from '../../components/SectionContent'
import Spin from '../../components/Spin'
import {
  fetchStorageStatus,
  fetchBulkPreview,
  startBulkRegenerate,
  resetCvJobs,
} from '../../actions/cvJobs'

const { Option } = Select

class CvRegenerate extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      selectedDays: 7,
    }
  }

  componentDidMount() {
    this.props.fetchStorageStatus()
  }

  componentWillUnmount() {
    this.props.resetCvJobs()
  }

  handleDaysChange = (value) => {
    this.setState({ selectedDays: value })
  }

  handlePreview = () => {
    this.props.fetchBulkPreview(this.state.selectedDays)
  }

  handleStartRegenerate = () => {
    if (!this.props.preview || this.props.preview.totalMembers === 0) {
      message.warning('No members to regenerate. Please preview first.')
      return
    }
    this.props.startBulkRegenerate(this.state.selectedDays)
  }

  renderStorageStatus() {
    const { storageConfigured, storageType, isLoadingStorageStatus, storageStatusError } = this.props
    
    if (isLoadingStorageStatus) {
      return <Spin />
    }
    
    if (storageStatusError) {
      return <Alert type="error" message={`Error: ${storageStatusError}`} />
    }
    
    if (!storageConfigured) {
      return (
        <Alert 
          type="warning" 
          message="Storage Not Configured"
          description="R2 or S3 storage is not configured. CV regeneration will not work until storage is set up."
        />
      )
    }
    
    return (
      <Alert 
        type="success" 
        message={`Storage Configured (${storageType?.toUpperCase()})`}
        description="CV files will be uploaded to the configured storage."
      />
    )
  }

  renderPreview() {
    const { preview, isLoadingPreview, previewError } = this.props
    
    if (isLoadingPreview) {
      return <Spin />
    }
    
    if (previewError) {
      return <Alert type="error" message={previewError} />
    }
    
    if (!preview) {
      return null
    }
    
    const columns = [
      { title: 'Member', dataIndex: 'snag', key: 'snag' },
      { title: 'Last Updated', dataIndex: 'updatedAt', key: 'updatedAt' },
      { title: 'CVs to Generate', dataIndex: 'cvCount', key: 'cvCount' },
    ]
    
    return (
      <div style={{ marginTop: 24 }}>
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={8}>
            <Card>
              <SimpleStatistic title="Members to Process" value={preview.totalMembers} />
            </Card>
          </Col>
          <Col span={8}>
            <Card>
              <SimpleStatistic title="Total CVs to Generate" value={preview.totalCvs} />
            </Card>
          </Col>
          <Col span={8}>
            <Card>
              <SimpleStatistic title="Days Selected" value={preview.days} />
            </Card>
          </Col>
        </Row>
        
        <Table 
          dataSource={preview.members} 
          columns={columns} 
          rowKey="id"
          pagination={{ pageSize: 10 }}
          size="small"
        />
      </div>
    )
  }

  renderProgress() {
    const { progress, isComplete, isStartingJob } = this.props
    
    if (isStartingJob) {
      return <Spin />
    }
    
    if (!progress) {
      return null
    }
    
    const isBulk = progress.type === 'bulk'
    const percent = isBulk 
      ? Math.round((progress.completedCvs / progress.totalCvs) * 100)
      : Math.round((progress.completed / progress.total) * 100)
    
    return (
      <div style={{ marginTop: 24 }}>
        <Card title="Generation Progress">
          <Progress 
            percent={percent} 
            status={isComplete ? (progress.errors?.length > 0 ? 'exception' : 'success') : 'active'}
          />
          
          {isBulk ? (
            <>
              <p><strong>Current Member:</strong> {progress.currentMember}</p>
              <p><strong>Current CV:</strong> {progress.currentCv}</p>
              <p><strong>Progress:</strong> {progress.completedMembers}/{progress.totalMembers} members, {progress.completedCvs}/{progress.totalCvs} CVs</p>
              {progress.estimatedRemaining && (
                <p><strong>Estimated Remaining:</strong> {progress.estimatedRemaining}</p>
              )}
            </>
          ) : (
            <>
              <p><strong>Status:</strong> {progress.current}</p>
              <p><strong>Progress:</strong> {progress.completed}/{progress.total} CVs</p>
            </>
          )}
          
          {isComplete && (
            <Alert 
              type={progress.errors?.length > 0 ? 'warning' : 'success'}
              message={progress.errors?.length > 0 
                ? `Completed with ${progress.errors.length} error(s)` 
                : 'All CVs generated successfully!'
              }
              style={{ marginTop: 16 }}
            />
          )}
          
          {progress.errors?.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <h4>Errors:</h4>
              <ul>
                {progress.errors.map((err, i) => (
                  <li key={i}>{err.memberSnag}: {err.error}</li>
                ))}
              </ul>
            </div>
          )}
        </Card>
      </div>
    )
  }

  render() {
    const { storageConfigured, currentJob, isComplete } = this.props
    const { selectedDays } = this.state
    const isJobRunning = currentJob && !isComplete
    
    return (
      <AdminLayout>
        <SectionHeader>
          <SectionHeaderTemplate
            breadcrumbRoutes={[
              { path: '/admin', title: 'Home' },
              { title: 'CV Regeneration' }
            ]}
            title="Bulk CV Regeneration"
          />
        </SectionHeader>
        <SectionContent>
          {this.renderStorageStatus()}
          
          {storageConfigured && (
            <>
              <Card title="Regenerate CVs for Recently Edited Members" style={{ marginTop: 24 }}>
                <div style={{ marginBottom: 16 }}>
                  <span style={{ marginRight: 8 }}>Members edited in the last:</span>
                  <Select 
                    value={selectedDays} 
                    onChange={this.handleDaysChange}
                    style={{ width: 140, marginRight: 16 }}
                    disabled={isJobRunning}
                  >
                    <Option value={0}>All members</Option>
                    <Option value={1}>1 day</Option>
                    <Option value={3}>3 days</Option>
                    <Option value={7}>7 days</Option>
                    <Option value={14}>14 days</Option>
                    <Option value={30}>30 days</Option>
                    <Option value={60}>60 days</Option>
                    <Option value={90}>90 days</Option>
                  </Select>
                  <Button 
                    onClick={this.handlePreview} 
                    style={{ marginRight: 8 }}
                    disabled={isJobRunning}
                  >
                    Preview
                  </Button>
                  <Button 
                    type="primary" 
                    onClick={this.handleStartRegenerate}
                    disabled={isJobRunning || !this.props.preview}
                  >
                    Start Regeneration
                  </Button>
                </div>
              </Card>
              
              {this.renderPreview()}
              {this.renderProgress()}
            </>
          )}
        </SectionContent>
      </AdminLayout>
    )
  }
}

const mapStateToProps = (state) => ({
  storageConfigured: state.cvJobs.storageConfigured,
  storageType: state.cvJobs.storageType,
  isLoadingStorageStatus: state.cvJobs.isLoadingStorageStatus,
  storageStatusError: state.cvJobs.storageStatusError,
  preview: state.cvJobs.preview,
  isLoadingPreview: state.cvJobs.isLoadingPreview,
  previewError: state.cvJobs.previewError,
  currentJob: state.cvJobs.currentJob,
  isStartingJob: state.cvJobs.isStartingJob,
  startJobError: state.cvJobs.startJobError,
  progress: state.cvJobs.progress,
  isComplete: state.cvJobs.isComplete,
})

const mapDispatchToProps = (dispatch) => ({
  fetchStorageStatus: () => dispatch(fetchStorageStatus()),
  fetchBulkPreview: (days) => dispatch(fetchBulkPreview(days)),
  startBulkRegenerate: (days) => dispatch(startBulkRegenerate(days)),
  resetCvJobs: () => dispatch(resetCvJobs()),
})

export default connect(mapStateToProps, mapDispatchToProps)(CvRegenerate)
